import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {filterTasks, loadTasks} from '../api/monthlyRecordingApi';
import {getMessage} from '../utils/messages';
import type {Config, TaskOption, TaskWithChildren} from '../types/types';

type TaskPickerProps = {
	config: Config;
	disabled?: boolean;
	onSelect: (task: TaskOption) => void;
	selectedTask?: TaskOption;
};

const ROOT_KEY = 'root';
const MIN_QUERY_LENGTH = 3;
type TaskTree = Record<string, TaskOption[]>;

const sortTasks = <T extends TaskOption>(tasks: T[]): T[] => [...tasks].sort((a, b) => a.breadCrumbs.localeCompare(b.breadCrumbs));

export const TaskPicker: React.FC<TaskPickerProps> = ({config, disabled, onSelect, selectedTask}) => {
	const [tree, setTree] = useState<TaskTree>({});
	const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
	const [loadingKeys, setLoadingKeys] = useState<Set<string>>(new Set());
	const [searchTerm, setSearchTerm] = useState('');
	const [searchResults, setSearchResults] = useState<TaskWithChildren[]>([]);
	const [searchExpandedIds, setSearchExpandedIds] = useState<Set<number>>(new Set());
	const [isOpen, setIsOpen] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const normalizedSearch = searchTerm.trim().toLowerCase();
	const searchActive = normalizedSearch.length >= MIN_QUERY_LENGTH;

	const fetchChildren = useCallback(async (parentKey: string, parentId?: number) => {
		let shouldLoad = true;
		setLoadingKeys(prev => {
			if (prev.has(parentKey)) {
				shouldLoad = false;
				return prev;
			}
			const next = new Set(prev);
			next.add(parentKey);
			return next;
		});
		if (!shouldLoad) {
			return;
		}
		try {
			const nodes = await loadTasks(config, parentId);
			setTree(prev => ({...prev, [parentKey]: sortTasks(nodes)}));
			setError(null);
		} catch (e) {
			setError((e as Error).message);
		} finally {
			setLoadingKeys(prev => {
				const next = new Set(prev);
				next.delete(parentKey);
				return next;
			});
		}
	}, [config]);

	useEffect(() => {
		void fetchChildren(ROOT_KEY);
	}, [fetchChildren]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
				setIsOpen(false);
				setSearchTerm('');
			}
		};
		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [isOpen]);

	useEffect(() => {
		if (!searchActive) {
			setSearchResults([]);
			return;
		}
		let cancelled = false;
		void filterTasks(config, normalizedSearch)
			.then(tasks => {
				if (!cancelled) {
					setSearchResults(tasks);
					setError(null);
				}
			})
			.catch(e => {
				if (!cancelled) {
					setError((e as Error).message);
					setSearchResults([]);
				}
			});
		return () => {
			cancelled = true;
		};
	}, [config, normalizedSearch, searchActive]);

	const searchTree = useMemo<TaskTree>(() => {
		const nodesByParent: TaskTree = {};
		const buildTree = (nodes: TaskWithChildren[], parentKey: string) => {
			if (!nodes.length) {
				return;
			}
			nodesByParent[parentKey] = nodes;
			nodes.forEach(child => {
				const childNodes = child.children;
				if (childNodes?.length) {
					buildTree(childNodes, child.id.toString());
				}
			});
		};
		if (searchResults.length) {
			buildTree(searchResults, ROOT_KEY);
		}
		return nodesByParent;
	}, [searchResults]);

	useEffect(() => {
		if (!searchActive) {
			setSearchExpandedIds(new Set());
			return;
		}
		const collectIds = (nodes: TaskWithChildren[], acc: Set<number>): Set<number> => {
			for (const node of nodes) {
				const childNodes = node.children;
				if (childNodes?.length) {
					acc.add(node.id);
					collectIds(childNodes, acc);
				}
			}
			return acc;
		};
		setSearchExpandedIds(collectIds(searchResults, new Set()));
	}, [searchActive, searchResults]);

	const toggleExpand = (task: TaskOption, expandOnly = false) => {
		const next = new Set(expandedIds);
		const isExpanded = next.has(task.id);
		if (isExpanded) {
			next.delete(task.id);
		} else {
			next.add(task.id);
			if (!tree[task.id.toString()]) {
				void fetchChildren(task.id.toString(), task.id);
			}
		}
		setExpandedIds(next);
	};

	const renderTaskNodes = (
		nodesByParent: TaskTree,
		expandedSet: Set<number>,
		onToggle: (task: TaskOption, expandOnly?: boolean) => void,
		parentKey: string,
		depth = 0,
		loadingSet?: Set<string>
	): React.ReactNode => {
		const nodes = nodesByParent[parentKey];
		if (!nodes?.length) {
			return null;
		}
		return nodes.map(node => {
			const childKey = node.id.toString();
			const hasChildren = Boolean(nodesByParent[childKey]?.length || (node.numberOfChildren && node.numberOfChildren > 0));
			const isExpanded = expandedSet.has(node.id);
			return (
				<div key={`${parentKey}-${node.id}`} className="task-picker-node mb-2">
					<div style={{
						display: 'flex',
						padding: '4px 8px',
						paddingLeft: 12 + depth * 16,
						cursor: node.bookable === false ? (hasChildren ? 'pointer' : 'default') : 'pointer',
						whiteSpace: 'nowrap',
					}}>
						{hasChildren ? (
							<button
								type="button"
								className="btn btn-link btn-xs p-0"
								onClick={() => onToggle(node)}
								aria-label={getMessage('monthlyRecording.taskPicker.expand')}
								style={{marginRight: 5}}
							>
								<i className={`fal ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'}`}></i>
							</button>
						) : (
							<span style={{display: 'inline-block', marginRight: 5, width: 18}}></span>
						)}
						<button
							type="button"
							className="btn btn-link btn-xs text-left p-0"
							onClick={() => {
								if (node.bookable === false) {
									if (hasChildren) {
										onToggle(node);
									}
									return;
								}
								onSelect(node);
								setIsOpen(false);
								setSearchTerm('');
							}}
							style={{flex: 1, whiteSpace: 'normal', wordBreak: 'break-word', width: '100%', textAlign: 'left'}}
						>
							{node.bookable === false ?
								<span className="text-muted">{node.name}</span> : <span>{node.name}</span>}
						</button>
					</div>
					{hasChildren && isExpanded && (
						<div style={{marginLeft: 15}}>
							{renderTaskNodes(nodesByParent, expandedSet, onToggle, childKey, depth + 1, loadingSet)}
							{loadingSet?.has(childKey) &&
								<div className="text-muted small pl-20">
									{getMessage('monthlyRecording.taskPicker.loadingChildren')}
								</div>}
						</div>
					)}
				</div>
			);
		});
	};

	const toggleSearchExpand = (task: TaskOption) => {
		setSearchExpandedIds(prev => {
			const isExpanded = prev.has(task.id);
			if (isExpanded) {
				const next = new Set(prev);
				next.delete(task.id);
				return next;
			} else {
				const next = new Set(prev);
				next.add(task.id);
				return next;
			}
		});
	};

	const displayValue = useMemo(() => (isOpen ? searchTerm : selectedTask?.name ?? ''), [isOpen, searchTerm, selectedTask?.name]);
	const descriptionPrimary = selectedTask?.name ?? getMessage('monthlyRecording.taskPicker.placeholder');
	const descriptionSecondary = selectedTask?.breadCrumbs;
	const showDescription = !isOpen && !searchTerm;
	const isPlaceholder = !selectedTask?.name;

	const toggleDropdown = () => {
		if (disabled) {
			return;
		}
		setIsOpen(prev => {
			const next = !prev;
			if (!next) {
				setSearchTerm('');
			}
			return next;
		});
	};

	return (
		<div
			ref={containerRef}
			className={`dropdown${isOpen ? ' open' : ''}`}
		>
			<div className="input-group">
				<div className="tree-dropdown-input" style={{flex: 1}}>
					<input
						ref={inputRef}
						type="text"
						className="form-control tree-dropdown-query"
						autoComplete="off"
						placeholder={getMessage('monthlyRecording.taskPicker.placeholder')}
						value={displayValue}
						disabled={disabled}
						onFocus={() => !disabled && setIsOpen(true)}
						onChange={event => {
							if (disabled) {
								return;
							}
							if (!isOpen) {
								setIsOpen(true);
							}
							setSearchTerm(event.target.value);
						}}
						readOnly={disabled}
					/>
					<div
						className={`tree-dropdown-description form-control${isOpen ? ' open' : ''} text-left${isPlaceholder ? ' text-muted' : ''}`}
						title={descriptionSecondary}
						style={{
							display: showDescription ? 'block' : 'none',
							cursor: disabled ? 'default' : 'text'
						}}
						onClick={() => {
							if (disabled) {
								return;
							}
							setIsOpen(true);
							inputRef.current?.focus();
						}}
					>
						<div style={isPlaceholder ? {marginTop: 0} : undefined}>{descriptionPrimary}</div>
						{descriptionSecondary &&
							<div className="text-muted small">{descriptionSecondary}</div>}
					</div>
				</div>
				<span className="input-group-btn">
					<button
						type="button"
						className="btn btn-default btn-icon tree-dropdown-caret text-left"
						onClick={toggleDropdown}
						disabled={disabled}
						style={{height: '100%', minWidth: 48, width: '100%'}}
					>
						<i className={`fal ${isOpen ? 'fa-caret-up' : 'fa-caret-down'}`}></i>
					</button>
				</span>
			</div>

			{isOpen && (
				<div className="dropdown-menu right-aligned dropdown-content" style={{width: '100%', padding: 0}}>
					<div className="tree-default tree-dropdown-tree text-left"
						 style={{maxHeight: 350, overflowY: 'auto', paddingLeft: 0, paddingRight: 12}}>
						{error &&
							<div className="text-danger small mb-5">{getMessage('monthlyRecording.taskPicker.error.load')}</div>}
						{searchTerm && !searchActive && (
							<div className="text-muted small mb-5">{getMessage('monthlyRecording.taskPicker.minQuery')}</div>
						)}
						{searchActive ? (
							<div className="p-0">
								{searchResults.length === 0 && (
									<div className="text-muted">{getMessage('monthlyRecording.taskPicker.noResults')}</div>
								)}
								{renderTaskNodes(searchTree, searchExpandedIds, toggleSearchExpand, ROOT_KEY)}
							</div>
						) : (
							<div className="text">
								{!tree[ROOT_KEY] && loadingKeys.has(ROOT_KEY) &&
									<div className="text-muted">{getMessage('monthlyRecording.taskPicker.loading')}</div>}
								{renderTaskNodes(tree, expandedIds, toggleExpand, ROOT_KEY, 0, loadingKeys)}
								{tree[ROOT_KEY] && tree[ROOT_KEY].length === 0 && !loadingKeys.has(ROOT_KEY) && (
									<div className="text-muted">{getMessage('monthlyRecording.taskPicker.empty')}</div>
								)}
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
};
